import { FilterOutlined } from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";
import { Spiner } from "@components/loaders/spiner";
import {
  HistoryOrder,
  HistoryOrderDetail,
  Order,
} from "@services/dtos/marketplace.dto";
import { MarketplaceService } from "@services/marketplace.service";
import { usePageHeader } from "@hooks/use_page_header";
import { useQuery } from "@tanstack/react-query";
import { toRp } from "@utils/helper";
import { linkPage } from "@utils/router";
import { Button, Select } from "antd";
import moment from "moment";
import { useState } from "react";
import { LuInbox } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

const MarketplaceHistoryPage = () => {
  const navigate = useNavigate();
  usePageHeader("Pesanan", { checkout: true, bottomNavigation: "hidden" });

  const [popupFilter, setPopupFilter] = useState<boolean>(false);

  const [range, setRange] = useState<"WEEK" | "MONTH">("WEEK");
  const [status, setStatus] = useState<
    | "ALL"
    | "KONFIRMASI"
    | "DIPROSES"
    | "DIKIRIM"
    | "DITERIMA PETUGAS"
    | "DITERIMA SANTRI"
    | "DIBATALKAN"
  >("ALL");

  const { data, isPending } = useQuery({
    queryKey: [
      MarketplaceService.queries.GET_MARKETPLACE_ORDERS,
      range,
      status,
    ],
    queryFn: () => MarketplaceService.historyOrders({ range, status }),
  });

  const merged = Object.values(
    (data?.data ?? []).reduce(
      (acc: { [key: string]: any }, item: HistoryOrder) => {
        if (!acc[item.trxId]) {
          // First time seeing this trxId
          acc[item.trxId] = {
            trxId: item.trxId,
            orderAt: item.orderAt,
            totalHargaPesanan: 0,
            fee: 0,
            detailPesanan: [],
            statusOrder: item.statusOrder,
          };
        }

        // Add totals and details
        acc[item.trxId].totalHargaPesanan += item.totalHargaPesanan;
        acc[item.trxId].fee += item.fee;
        acc[item.trxId].detailPesanan = acc[item.trxId].detailPesanan.concat(
          item.detailPesanan || []
        );

        return acc;
      },
      {}
    )
  );


  const onOpenDetail = (order: HistoryOrder) => {
    navigate(linkPage.MARKETPLACE_HISTORY_DETAIL, { state: order });
  };

  return (
    <>
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        {/* set filter */}
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <div className="tw-text-sm tw-text-text-secondary">
            Data{" "}
            <span className="tw-font-semibold tw-text-text-primary">
              {range === "WEEK" ? "7" : "30"} hari terakhir
            </span>
          </div>
          <FilterOutlined
            className="tw-p-2 tw-text-xl tw-bg-transparent card-body"
            onClick={() => setPopupFilter(true)}
          />
        </div>
        {/* end of set filter */}

        {/* report */}
        <div className="tw-mt-8">
          {isPending ? (
            <div className="tw-text-center card-body">
              <Spiner />
            </div>
          ) : merged != null && merged.length > 0 ? (
            <>
              {merged
                .sort(
                  (a, b) =>
                    new Date(b.orderAt).getTime() -
                    new Date(a.orderAt).getTime()
                )
                .map((item, idx) => (
                  <div
                    key={idx}
                    className="tw-mb-4 card-body"
                    onClick={() => onOpenDetail(item)}
                  >
                    <div className="tw-flex tw-justify-between tw-items-center tw-font-semibold tw-text-text-secondary">
                      <div
                        className={`tw-inline-flex tw-items-center tw-rounded-md tw-px-2 tw-py-1 tw-text-[10px] tw-font-medium tw-ring-1 tw-ring-inset ${
                          item.statusOrder === "DIBATALKAN"
                            ? "tw-bg-red-50 tw-text-red-700 tw-ring-red-600/10"
                            : item.statusOrder === "DITERIMA SANTRI"
                            ? "tw-bg-green-50 tw-text-green-700 tw-ring-green-600/10"
                            : item.statusOrder === "DITERIMA PETUGAS"
                            ? "tw-bg-magenta-50 tw-text-magenta-700 tw-ring-magenta-600/10"
                            : item.statusOrder === "DIKIRIM"
                            ? "tw-bg-green-50 tw-text-green-700 tw-ring-green-600/10"
                            : item.statusOrder === "DIPROSES"
                            ? "tw-bg-blue-50 tw-text-blue-700 tw-ring-blue-600/10"
                            : "tw-bg-slate-50 tw-text-slate-700 tw-ring-slate-600/10"
                        }`}
                      >
                        {item.statusOrder}
                      </div>

                      <div className="tw-w-1/2 tw-text-[10px] tw-font-medium tw-text-end tw-text-text-secondary">
                        {moment(item.orderAt ?? Date.now()).format(
                          "DD MMMM YYYY || HH:mm"
                        )}
                      </div>
                    </div>
                    <div
                      className="tw-py-1 tw-my-2 tw-font-semibold tw-border-[#630d5f33]"
                      style={{
                        borderBottom: "1px solid",
                        borderTop: "1px solid",
                      }}
                    >
                      {item.detailPesanan.map(
                        (detail: HistoryOrderDetail, idOrder: number) =>
                          idOrder === 0 && (
                            <div
                              key={idOrder}
                              className="tw-flex tw-justify-between tw-items-center tw-text-text-primary"
                            >
                              <div className="tw-text-sm tw-line-clamp-1 tw-w-[90%]">
                                {detail.namaBarang}
                              </div>
                              <div className="tw-text-xs tw-text-end">
                                x{detail.jumlahBarang}
                              </div>
                            </div>
                          )
                      )}
                      {item.detailPesanan.length > 1 && (
                        <div className="tw-text-[10px] tw-text-text-secondary">
                          +{item.detailPesanan.length - 1} pesanan lainnya
                        </div>
                      )}
                    </div>

                    <div className="tw-flex tw-justify-between tw-items-start">
                      <div className="total-wrapper tw-w-1/2">
                        <div className="tw-text-[10px]">Total Harga :</div>
                        <div className="tw-text-sm tw-font-bold tw-text-text-primary">
                          {toRp(
                            (item.totalHargaPesanan ?? 0) + (item.fee ?? 0)
                          )}
                        </div>
                      </div>
                      <div className="tw-text-[10px] tw-text-end tw-text-text-tertiary tw-italic">
                        klik untuk melihat detail
                      </div>
                    </div>
                  </div>
                ))}
            </>
          ) : (
            <div className="tw-p-8 tw-text-center card-body">
              <LuInbox className="tw-mx-auto tw-mb-2 tw-text-5xl tw-text-text-secondary tw-opacity-40" />
              <div>Data tidak ditemukan</div>
            </div>
          )}
        </div>
        {/* end of report */}
      </div>

      <PopupCustom
        title="Filter Data"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <div className="">
          <div className="tw-text-xs tw-mb-1 tw-text-text-secondary">
            Filter Range
          </div>
          <Select
            className=" tw-text-base tw-mb-4"
            placeholder="Pilih data ditampilkan"
            defaultValue={range}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setRange(e as "WEEK" | "MONTH")}
            options={[
              { value: "WEEK", label: "7 hari terakhir" },
              { value: "MONTH", label: "30 hari terakhir" },
            ]}
          ></Select>

          <div className="tw-text-xs tw-mb-1 tw-text-text-secondary">
            Status Pesanan
          </div>
          <Select
            className=" tw-text-base"
            placeholder="Pilih status pesanan"
            defaultValue={status}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) =>
              setStatus(
                e as
                  | "ALL"
                  | "KONFIRMASI"
                  | "DIPROSES"
                  | "DIKIRIM"
                  | "DITERIMA PETUGAS"
                  | "DITERIMA SANTRI"
                  | "DIBATALKAN"
              )
            }
            options={[
              { value: "ALL", label: "Semua" },
              { value: "KONFIRMASI", label: "Konfirmasi" },
              { value: "DIPROSES", label: "Diproses" },
              { value: "DIKIRIM", label: "Dikirim" },
              { value: "DITERIMA PETUGAS", label: "Diterima Petugas" },
              { value: "DITERIMA SANTRI", label: "Diterima Santri" },
              { value: "DIBATALKAN", label: "Dibatalkan" },
            ]}
          ></Select>

          <Button
            onClick={() => setPopupFilter(false)}
            className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-sm tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
            style={{ background: "#ef4444" }}
          >
            Tutup
          </Button>
        </div>
      </PopupCustom>
    </>
  );
};

export default MarketplaceHistoryPage;
